import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaArrowLeft, FaTrash, FaPaperclip } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const StaffFeedbackForm = ({ homeId, homeDetails, onBack }) => {
  const [formData, setFormData] = useState({
    isAnonymous: false,
    fullName: '',
    employeeId: '',
    department: '',
    role: '',
    employmentType: '',
    feedbackType: '',
    feedbackDetails: '',
    severityLevel: '',
    relatedArea: '',
    incidentDate: '',
    incidentTime: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const signaturePad = useRef(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // Check total files limit (max 5 files)
    if (uploadedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    setUploading(true);

    try {
      for (const file of files) {
        // Check file size (max 5MB per file)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 5MB limit`);
          continue;
        }

        // Convert file to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to Vercel Blob
        const response = await axios.post(`${API_URL}/staff-feedback/upload`, {
          fileName: file.name,
          fileData: base64,
          fileType: file.type
        });

        setUploadedFiles(prev => [...prev, response.data]);
        toast.success(`${file.name} uploaded successfully`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    toast.info('File removed');
  };

  const clearSignature = () => {
    signaturePad.current.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.isAnonymous) {
      if (!formData.fullName || !formData.employeeId || !formData.department) {
        toast.error('Please fill in all required staff identification fields');
        return;
      }
    }

    if (!formData.employmentType) {
      toast.error('Please select employment type');
      return;
    }

    if (!formData.feedbackType) {
      toast.error('Please select feedback type');
      return;
    }

    if (!formData.severityLevel) {
      toast.error('Please select severity level');
      return;
    }

    if (!formData.feedbackDetails || formData.feedbackDetails.trim().length < 10) {
      toast.error('Please provide detailed feedback (at least 10 characters)');
      return;
    }

    if (signaturePad.current.isEmpty()) {
      toast.error('Please provide your digital signature');
      return;
    }

    const signature = signaturePad.current.toDataURL();

    console.log({
        ...formData,
        homeId,
        supportingDocuments: uploadedFiles,
        digitalSignature: signature,
        consentGiven: true
    },'asdbkakjbdkjabskdbkaj')
    try {
      const response = await axios.post(`${API_URL}/staff-feedback`, {
        ...formData,
        homeId,
        supportingDocuments: uploadedFiles,
        digitalSignature: signature,
        consentGiven: true
      });

      setSubmitted(true);
      toast.success('Staff feedback submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit feedback');
    }
  };

  if (submitted) {
    return (
      <div className="log-form-container">
        <div className="log-form-card">
          <div className="success-message">
            <div className="success-icon"><FaCheckCircle /></div>
            <h2 className="success-title">Thank You!</h2>
            <p className="success-text">
              Your staff feedback has been submitted successfully. 
              {formData.severityLevel === 'Critical' || formData.feedbackType === 'Safety Concern' 
                ? ' This has been flagged for immediate attention.' 
                : ' We will review your feedback and take appropriate action.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="log-form-container">
      <div className="log-form-card">
        <div className="log-form-header">
          <img src="/littleFeetlogo.webp" alt="Little Feet Homes" className="log-form-logo" />
          <h1 className="log-form-title">Staff Feedback</h1>
          <p className="log-form-subtitle">{homeDetails.title}</p>
          <button onClick={onBack} className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Anonymous Toggle */}
          <div className="anonymous-toggle" style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--light-gray)', borderRadius: '0.5rem', border: '2px solid var(--primary)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isAnonymous}
                onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, fontSize: '1rem' }}>Submit Anonymously</span>
            </label>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem', marginLeft: '2rem' }}>
              Your identity will be kept completely confidential. We encourage anonymous feedback for complaints and concerns.
            </p>
          </div>

          {/* Staff Identification - Hidden if Anonymous */}
          {!formData.isAnonymous && (
            <>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Staff Identification</h3>
              
              <div className="form-group">
                <label className="form-label required">Full Name</label>
                <input 
                  type="text" 
                  name="fullName" 
                  className="form-input" 
                  value={formData.fullName} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Employee ID</label>
                <input 
                  type="text" 
                  name="employeeId" 
                  className="form-input" 
                  value={formData.employeeId} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter your employee ID"
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Department</label>
                <select 
                  name="department" 
                  className="form-select" 
                  value={formData.department} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                >
                  <option value="">Select department</option>
                  <option value="Nursing">Nursing</option>
                  <option value="Care Support">Care Support</option>
                  <option value="Administration">Administration</option>
                  <option value="Kitchen">Kitchen</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Security">Security</option>
                  <option value="Manager">Manager</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          )}

          {/* Employment Information */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Employment Information</h3>

          <div className="form-group">
            <label className="form-label">Role / Designation</label>
            <input 
              type="text" 
              name="role" 
              className="form-input" 
              value={formData.role} 
              onChange={handleChange}
              placeholder="e.g., Nurse, Caregiver, Cook"
            />
          </div>

          <div className="form-group">
            <label className="form-label required">Employment Type</label>
            <div className="radio-group">
              {['Full-Time', 'Part-Time', 'Contract', 'Volunteer'].map((type) => (
                <label key={type} className={`radio-option ${formData.employmentType === type ? 'selected' : ''}`}>
                  <input 
                    type="radio" 
                    name="employmentType" 
                    value={type} 
                    checked={formData.employmentType === type} 
                    onChange={handleChange}
                    required
                  />
                  {type}
                </label>
              ))}
            </div>
          </div>

          {/* Feedback Type & Severity */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Feedback Details</h3>

          <div className="form-group">
            <label className="form-label required">Type of Feedback</label>
            <select 
              name="feedbackType" 
              className="form-select" 
              value={formData.feedbackType} 
              onChange={handleChange} 
              required
            >
              <option value="">Select feedback type</option>
              <option value="Workplace Suggestion">Workplace Suggestion</option>
              <option value="Operational Improvement">Operational Improvement</option>
              <option value="Complaint">Complaint</option>
              <option value="Safety Concern">Safety Concern</option>
              <option value="Resident Welfare Concern">Resident Welfare Concern</option>
              <option value="HR Issue">HR Issue</option>
              <option value="Appreciation">Appreciation</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label required">Severity Level</label>
            <div className="radio-group">
              {[
                { value: 'Low', color: '#10b981' },
                { value: 'Medium', color: '#f59e0b' },
                { value: 'High', color: '#ef4444' },
                { value: 'Critical', color: '#dc2626' }
              ].map((level) => (
                <label 
                  key={level.value} 
                  className={`radio-option ${formData.severityLevel === level.value ? 'selected' : ''}`}
                  style={formData.severityLevel === level.value ? { borderColor: level.color, background: `${level.color}15` } : {}}
                >
                  <input 
                    type="radio" 
                    name="severityLevel" 
                    value={level.value} 
                    checked={formData.severityLevel === level.value} 
                    onChange={handleChange}
                    required
                  />
                  {level.value}
                </label>
              ))}
            </div>
            {(formData.severityLevel === 'Critical' || formData.feedbackType === 'Safety Concern') && (
              <p style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 600 }}>
                ⚠️ This will be automatically escalated to management
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Related Area</label>
            <select 
              name="relatedArea" 
              className="form-select" 
              value={formData.relatedArea} 
              onChange={handleChange}
            >
              <option value="">Select related area (optional)</option>
              <option value="Staffing Levels">Staffing Levels</option>
              <option value="Workload">Workload</option>
              <option value="Equipment">Equipment</option>
              <option value="Facility Condition">Facility Condition</option>
              <option value="Resident Care">Resident Care</option>
              <option value="Management">Management</option>
              <option value="Communication">Communication</option>
              <option value="Training">Training</option>
              <option value="Safety & Compliance">Safety & Compliance</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label required">Description / Details</label>
            <textarea 
              name="feedbackDetails" 
              className="form-textarea" 
              value={formData.feedbackDetails} 
              onChange={handleChange}
              placeholder="Please describe the issue, suggestion, or concern clearly. Provide as much detail as possible to help us understand and address your feedback."
              required
              maxLength={3000}
              style={{ minHeight: '150px' }}
            />
            <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
              {formData.feedbackDetails.length} / 3000 characters
            </small>
          </div>

          {/* Incident Information */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Incident Information (Optional)</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Date of Incident</label>
              <input 
                type="date" 
                name="incidentDate" 
                className="form-input" 
                value={formData.incidentDate} 
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Time of Incident</label>
              <input 
                type="time" 
                name="incidentTime" 
                className="form-input" 
                value={formData.incidentTime} 
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Supporting Documents - Multiple Files */}
          <div className="form-group">
            <label className="form-label">Supporting Documents (Optional)</label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input 
                ref={fileInputRef}
                type="file" 
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx"
                className="form-input"
                disabled={uploading || uploadedFiles.length >= 5}
                multiple
                style={{ flex: 1 }}
              />
              <button 
                type="button" 
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-secondary"
                disabled={uploading || uploadedFiles.length >= 5}
              >
                <FaPaperclip /> Add Files
              </button>
            </div>
            <small style={{ color: 'var(--text-light)', fontSize: '0.875rem', display: 'block', marginTop: '0.5rem' }}>
              Upload photos or documents (Max 5 files, 5MB each) - Supported: Images, PDF, Word documents
            </small>
            {uploading && (
              <p style={{ fontSize: '0.875rem', color: 'var(--primary)', marginTop: '0.5rem', fontWeight: 600 }}>
                Uploading files...
              </p>
            )}
            
            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Uploaded Files ({uploadedFiles.length}/5):
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {uploadedFiles.map((file, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        padding: '0.75rem', 
                        background: 'var(--light-gray)', 
                        borderRadius: '0.375rem',
                        border: '1px solid var(--border-gray)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        <FaPaperclip style={{ color: 'var(--primary)' }} />
                        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{file.fileName}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>
                          ({(file.fileSize / 1024).toFixed(2)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="btn btn-danger btn-sm"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Digital Signature */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Digital Signature</h3>
          
          <div className="form-group">
            <label className="form-label required">Signature</label>
            <div className="signature-container">
              <SignatureCanvas
                ref={signaturePad}
                canvasProps={{ className: 'signature-canvas' }}
              />
              <div className="signature-controls">
                <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Sign above</span>
                <button type="button" onClick={clearSignature} className="btn btn-secondary btn-sm">Clear</button>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="form-group">
            <div className="checkbox-group">
              <input type="checkbox" id="consent" required />
              <label htmlFor="consent">
                I confirm this information is accurate and shared in good faith to improve workplace conditions.
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }} disabled={uploading}>
            {uploading ? 'Uploading files...' : 'Submit Staff Feedback'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StaffFeedbackForm;